import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  DollarSign, Clock, MapPin, ArrowRight, CheckCircle, Users, TrendingUp, 
  Briefcase, Zap, Shield, Star, Award, Calendar, Phone, Mail, MessageSquare,
  ChevronDown, Play, ExternalLink, Heart, Target, Globe, UserCheck, Search, FileText, Twitter, Instagram, Linkedin,
  ShoppingBag, Truck, Camera, Sparkles, BookOpen
} from 'lucide-react';
import NewsletterSignup from '../components/NewsletterSignup';
import apiService from '../services/api';
import './Home.css';

const Home = () => {
  const [activeJobs, setActiveJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedFAQ, setExpandedFAQ] = useState(null);

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        setLoading(true);
        const jobs = await apiService.getJobs({ active_only: true, limit: 6 });
        setActiveJobs(jobs || []);
      } catch (err) {
        console.error('Error fetching jobs:', err);
        setError('Failed to load job opportunities. Please try again later.');
        setActiveJobs([]);
      } finally {
        setLoading(false);
      }
    };

    fetchJobs();
  }, []);

  const features = [
    {
      icon: Zap,
      title: 'High-Paying Gigs',
      description: 'Earn $150-300 per day with flexible scheduling that fits your lifestyle.',
      stat: '$250'
    },
    {
      icon: Clock,
      title: 'Flexible Scheduling',
      description: 'Work when you want, where you want. Our gigs are designed to fit around your life.',
      stat: '24/7'
    },
    {
      icon: Shield,
      title: 'Verified Employers',
      description: 'All our employers are vetted and background-checked. Work with confidence.',
      stat: '100%'
    }
  ];

  const jobCategories = [
    { name: 'Event Staff', icon: Users, count: 45, avgPay: '$225', color: 'bg-blue-100 text-blue-600' },
    { name: 'Food Delivery', icon: Clock, count: 32, avgPay: '$180', color: 'bg-blue-100 text-blue-600' },
    { name: 'Retail Support', icon: ShoppingBag, count: 28, avgPay: '$160', color: 'bg-purple-100 text-purple-600' },
    { name: 'Moving Help', icon: Truck, count: 15, avgPay: '$200', color: 'bg-orange-100 text-orange-600' },
    { name: 'Photography', icon: Camera, count: 12, avgPay: '$175', color: 'bg-pink-100 text-pink-600' },
    { name: 'Cleaning Services', icon: Sparkles, count: 18, avgPay: '$150', color: 'bg-indigo-100 text-indigo-600' },
    { name: 'Pet Care', icon: Heart, count: 8, avgPay: '$140', color: 'bg-red-100 text-red-600' },
    { name: 'Tutoring', icon: BookOpen, count: 22, avgPay: '$180', color: 'bg-yellow-100 text-yellow-600' }
  ];

  const testimonials = [
    {
      name: 'Sarah Johnson',
      role: 'Event Staff',
      image: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face',
      quote: 'I earned $2,400 last month working events! NYC Gigs made it so easy to find flexible work that fits my schedule.',
      rating: 5,
      earnings: '$2,400/month'
    },
    {
      name: 'Michael Chen',
      role: 'Delivery Driver',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
      quote: 'The employers are all verified and professional. I\'ve never had any issues with payments or scheduling.',
      rating: 5,
      earnings: '$1,800/month'
    },
    {
      name: 'Emily Rodriguez',
      role: 'Photography Assistant',
      image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face',
      quote: 'Perfect for my photography career! I get to work with professionals and build my portfolio while earning great money.',
      rating: 5,
      earnings: '$2,100/month'
    }
  ];

  const faqs = [
    {
      question: 'How much can I earn with NYC Gigs?',
      answer: 'Our gigs typically pay between $150-300 per day, with some specialized positions paying up to $500. Most workers earn $1,500-3,000 per month working flexible hours.'
    },
    {
      question: 'What types of jobs are available?',
      answer: 'We offer a wide variety of opportunities including event staff, food delivery, retail support, moving help, photography, cleaning services, pet care, and tutoring. New categories are added regularly.'
    },
    {
      question: 'How do I apply for jobs?',
      answer: 'Simply browse our job listings, click on any opportunity that interests you, and you\'ll be redirected to a secure Google Form to submit your application. It takes less than 2 minutes!'
    },
    {
      question: 'Are the employers verified and trustworthy?',
      answer: 'Yes! All employers go through our rigorous verification process including background checks, business verification, and payment history reviews. We only work with legitimate, professional businesses.'
    },
    {
      question: 'What if I can\'t make it to a job I applied for?',
      answer: 'We understand life happens! Simply contact us at least 24 hours before the job start time, and we\'ll help you reschedule or find a replacement. We want to maintain good relationships with both workers and employers.'
    },
    {
      question: 'How do I get paid?',
      answer: 'Payment methods vary by employer but typically include direct deposit, PayPal, or check. Most payments are processed within 1-3 business days after job completion. We ensure all payment terms are clearly stated before you accept any position.'
    }
  ];

  const stats = [
    { number: '500+', label: 'Active Jobs', icon: Briefcase },
    { number: '2,000+', label: 'Happy Workers', icon: Users },
    { number: '50+', label: 'NYC Locations', icon: MapPin },
    { number: '95%', label: 'Success Rate', icon: Star }
  ];

  return (
    <div className="home">
      {/* Hero Section - High-Converting */}
      <section className="relative px-6 py-24 lg:py-32 bg-gradient-to-br from-gray-50 to-white overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        <div className="relative max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                <div className="inline-flex items-center px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-6">
                  <Star className="w-4 h-4 mr-2" />
                  Trusted by 2,000+ NYC Workers
                </div>
                <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-6 leading-tight">
                  Find Your Next
                  <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-blue-500">
                    NYC Gig
                  </span>
                </h1>
                <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-2xl mx-auto lg:mx-0 leading-relaxed">
                  Discover <span className="font-semibold text-gray-900">500+ flexible job opportunities</span> across New York City. 
                  From events to deliveries, find gigs that fit your schedule and pay <span className="font-semibold text-blue-600">$150-300 per day</span>.
                </p>
                
                {/* Trust Indicators */}
                <div className="flex flex-wrap justify-center lg:justify-start gap-6 mb-8">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-5 h-5 text-blue-500" />
                    <span className="text-gray-600">Verified Employers</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-5 h-5 text-blue-500" />
                    <span className="text-gray-600">Instant Apply</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-5 h-5 text-blue-500" />
                    <span className="text-gray-600">Flexible Scheduling</span>
                  </div>
                </div>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mt-8">
                  <a href="/jobs">
                    <button className="group bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 text-lg font-medium rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 flex items-center justify-center min-w-[180px]">
                      Browse Jobs
                      <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </button>
                  </a>
                  <a href="#how-it-works">
                    <button className="group border-2 border-gray-300 text-gray-700 hover:border-blue-300 hover:text-blue-700 px-8 py-4 text-lg font-medium rounded-xl transition-all duration-300 flex items-center justify-center min-w-[180px]">
                      How It Works
                      <Play className="ml-2 w-4 h-4" />
                    </button>
                  </a>
                </div>
              </motion.div>
            </div>
            
            {/* Hero Visual */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-3xl p-8 shadow-2xl">
                <div className="grid grid-cols-2 gap-4">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                      <div className="w-8 h-8 bg-blue-100 rounded-lg mb-3 flex items-center justify-center">
                        <Briefcase className="w-4 h-4 text-blue-600" />
                      </div>
                      <div className="h-2 bg-gray-200 rounded w-3/4 mb-2"></div>
                      <div className="h-2 bg-gray-200 rounded w-1/2"></div>
                    </div>
                  ))}
                </div>
                <div className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-blue-800">Active Jobs</p>
                      <p className="text-2xl font-bold text-blue-600">500+</p>
                    </div>
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-blue-600" />
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section - Social Proof */}
      <section className="px-6 py-16 bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="flex justify-center mb-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <stat.icon className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-1">{stat.number}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Value Proposition Section */}
      <section className="px-6 py-24 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
            >
              Why Choose NYC Gigs?
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl text-gray-600 max-w-3xl mx-auto"
            >
              We make it easy to find flexible work opportunities that fit your lifestyle and pay what you deserve.
            </motion.p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2"
              >
                <div className="flex justify-center mb-6">
                  <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center">
                    <feature.icon className="w-8 h-8 text-blue-600" />
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4 text-center">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-center mb-4">
                  {feature.description}
                </p>
                <div className="text-center">
                  <span className="inline-block bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-medium">
                    {feature.stat}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Job Categories Section */}
      <section id="jobs" className="px-6 py-24 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
            >
              Popular Job Categories
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl text-gray-600 max-w-3xl mx-auto"
            >
              Explore the most in-demand gig opportunities in NYC. Find your perfect match from hundreds of available positions.
            </motion.p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {jobCategories.map((category, index) => (
              <motion.div
                key={category.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group"
              >
                <a href={`#jobs?category=${encodeURIComponent(category.name)}`}>
                  <div className="bg-white border border-gray-200 rounded-2xl p-6 text-center hover:shadow-lg transition-all duration-300 group-hover:-translate-y-1 group-hover:border-blue-300">
                    <div className={`w-12 h-12 ${category.color} rounded-xl flex items-center justify-center mx-auto mb-4`}>
                      <category.icon className="w-6 h-6" />
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                      {category.name}
                    </h3>
                    <div className="text-sm text-gray-600">
                      <div className="font-medium text-blue-600">{category.avgPay}/day</div>
                      <div>{category.count} jobs</div>
                    </div>
                  </div>
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="px-6 py-24 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
            >
              How It Works
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl text-gray-600 max-w-3xl mx-auto"
            >
              Get started in minutes. Our simple 3-step process makes finding and applying for gigs effortless.
            </motion.p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: '01',
                title: 'Browse Jobs',
                description: 'Search through hundreds of opportunities filtered by location, pay, and schedule.',
                icon: Search
              },
              {
                step: '02',
                title: 'Apply Instantly',
                description: 'Click apply and fill out our secure Google Form. Takes less than 2 minutes!',
                icon: FileText
              },
              {
                step: '03',
                title: 'Get Hired',
                description: 'Receive confirmation and start working. Get paid quickly and securely.',
                icon: Award
              }
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="relative"
              >
                <div className="bg-white rounded-2xl p-8 shadow-lg text-center">
                  <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <item.icon className="w-8 h-8 text-blue-600" />
                  </div>
                  <div className="text-4xl font-bold text-blue-600 mb-4">{item.step}</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{item.description}</p>
                </div>
                {index < 2 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <ArrowRight className="w-8 h-8 text-blue-300" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Recent Jobs Section */}
      {!loading && activeJobs.length > 0 && (
        <section className="px-6 py-24 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <motion.h2
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
              >
                Recent Job Opportunities
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="text-xl text-gray-600 max-w-3xl mx-auto"
              >
                Check out the latest gigs available in NYC. New opportunities are added daily.
              </motion.p>
            </div>
            
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-8 text-center">
                <p className="text-red-800">{error}</p>
              </div>
            )}

            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {activeJobs.slice(0, 6).map((job, index) => (
                <motion.div
                  key={job.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 p-6"
                >
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">{job.title}</h3>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      job.is_active 
                        ? 'bg-blue-100 text-blue-800' 
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {job.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  
                  <div className="flex items-center space-x-4 text-sm text-gray-600 mb-3">
                    <div className="flex items-center space-x-1">
                      <MapPin className="w-4 h-4" />
                      <span>{job.location}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <DollarSign className="w-4 h-4" />
                      <span>{job.pay}</span>
                    </div>
                  </div>

                  <p className="text-gray-700 mb-4">
                    {job.description.length > 120 
                      ? `${job.description.substring(0, 120)}...` 
                      : job.description
                    }
                  </p>

                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-500">
                      Posted {new Date(job.created_at).toLocaleDateString()}
                    </span>
                    {job.is_active && (
                      <a
                        href={job.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        Apply Now
                      </a>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
            
            <div className="text-center mt-12">
              <a href="/jobs">
                <button className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 text-lg font-medium rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 flex items-center mx-auto">
                  View All Jobs
                  <ArrowRight className="ml-2 w-5 h-5" />
                </button>
              </a>
            </div>
          </div>
        </section>
      )}

      {/* Testimonials Section */}
      <section id="testimonials" className="px-6 py-24 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
            >
              Success Stories
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl text-gray-600 max-w-3xl mx-auto"
            >
              Hear from real NYC workers who have transformed their income with flexible gigs.
            </motion.p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="flex items-center mb-6">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover mr-4"
                  />
                  <div>
                    <h3 className="font-semibold text-gray-900">{testimonial.name}</h3>
                    <p className="text-sm text-gray-600">{testimonial.role}</p>
                  </div>
                  <div className="ml-auto">
                    <div className="flex items-center">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                      ))}
                    </div>
                  </div>
                </div>
                <blockquote className="text-gray-600 mb-4 italic">
                  "{testimonial.quote}"
                </blockquote>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Earnings</span>
                  <span className="font-semibold text-blue-600">{testimonial.earnings}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Employer Section */}
      <section className="px-6 py-24 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                For Employers
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Need reliable, qualified workers for your business? NYC Gigs connects you with thousands of pre-screened, motivated workers ready to help your business succeed.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-gray-700">Reach 2,000+ qualified workers</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-gray-700">Post jobs in minutes</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-gray-700">Only pay when you hire</span>
                </div>
              </div>

              <a href="/employer">
                <button className="bg-gray-900 hover:bg-gray-800 text-white px-8 py-4 text-lg font-medium rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                  Post a Job
                </button>
              </a>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-3xl p-8 shadow-2xl">
                <div className="space-y-4">
                  <div className="bg-white rounded-xl p-4 shadow-sm">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900">Event Staff Needed</p>
                        <p className="text-sm text-gray-600">Music Festival - Brooklyn</p>
                      </div>
                      <span className="bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs font-medium">
                        $225/day
                      </span>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900">Delivery Driver</p>
                        <p className="text-sm text-gray-600">Same Day - Manhattan</p>
                      </div>
                      <span className="bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs font-medium">
                        $150/day
                      </span>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900">Photography Assistant</p>
                        <p className="text-sm text-gray-600">Wedding - Queens</p>
                      </div>
                      <span className="bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs font-medium">
                        $175/day
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="px-6 py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
            >
              Frequently Asked Questions
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl text-gray-600"
            >
              Everything you need to know about working with NYC Gigs.
            </motion.p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-xl shadow-sm border border-gray-200"
              >
                <button
                  onClick={() => setExpandedFAQ(expandedFAQ === index ? null : index)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <span className="font-medium text-gray-900">{faq.question}</span>
                  <ChevronDown
                    className={`w-5 h-5 text-gray-500 transition-transform ${
                      expandedFAQ === index ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {expandedFAQ === index && (
                  <div className="px-6 pb-4">
                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="px-6 py-24 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Stay Updated
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Get notified about new opportunities first. Join 5,000+ NYC workers getting exclusive job alerts and tips.
            </p>
            
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8 border border-blue-200">
              <NewsletterSignup />
            </div>
          </motion.div>
        </div>
      </section>

      
    </div>
  );
};

export default Home; 